import { useEffect, useState } from 'react';

import { Checkbox, DropdownMenu } from '../../form';
import { ActionButton, Icon, Loading } from '../../UI';
import { AssetDetail } from './AssetDetail';
import { CopyTo } from './CopyTo';
import { Column } from '../../NewStrategy/StrategyTable';
import { CustomNumber } from '../../CustomNumber/CustomNumber';
import { DeleteAssetModal } from './DeleteAsset';
import { MultiSelected } from './MultiSelected';
import { ProfitLoss } from '../../ProfitLoss';
import { Sort } from '../../NewStrategy/Sort';

import { useModalContext } from '../../../context/Modal';
import { useUserStrategies } from '../../../context/UserStrategies';

import { INTERVALS } from '../../../hooks/useIntervals';
import { useAssetInputChanges } from '../../../hooks/useAssetInputChanges';
import { useSort } from '../../../hooks/useSort';

import {
  aggregateByGroupAndAsset,
  calculateWeightedAverage,
  computePercentage,
  formatAWSDate,
  getSymbol,
  getTotalValues,
  getVariation,
} from '../../../helpers/functions';

import { StrategyAssetsParentGroup } from '../types';
import { PortfolioProps } from './types';
import { SortType } from '../../NewStrategy/types';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { UpgradeSubscription } from '../../UpgradeSubscription/UpgradeSubscription';
import { useAuth } from '../../../context/Auth';
import { IStrategyAsset, TTickerCurrency } from '../../../types/model';
import { Asset } from '../../Asset/Asset';
import { logger } from '../../../helpers/logger';
import { UpsertAsset } from '../../Asset/UpsertAsset';
import { DuplicateAssetModal } from './DuplicateAsset';
import {
  AssetOptionEnum,
  ConnectionTypeEnum,
  CurrencyCodeEnum,
} from '../../../constants/enums';

export const PORTFOLIO_COLUMNS = [
  'ASSET',
  'QUANTITY',
  'ENTRY PRICE',
  'INVESTED',
  'LAST PRICE',
  'TOT VALUE',
  'INITIAL_ALLOCATION',
  'ALLOCATION',
  'VAR_ALL',
  'VAR_24H',
  'VAR_7D',
  'VAR_30D',
  'VAR_1Y',
] as const;

export const DEFAULT_PORTFOLIO_COLUMNS = PORTFOLIO_COLUMNS.filter(
  (c) => c !== 'INITIAL_ALLOCATION'
);

const canSortCategories = (type: SortType) =>
  [
    'ASSET',
    'INVESTED',
    'VAR_24H',
    'VAR_7D',
    'VAR_30D',
    'VAR_1Y',
    'VAR_ALL',
    'TOT VALUE',
    'INITIAL_ALLOCATION',
    'ALLOCATION',
  ].includes(type);

export const Portfolio = ({
  height,
  width,
  strategyId,
  useCheckboxes = true,
  columns = [],
  menuOptions = [],
}: PortfolioProps) => {
  const availableColumns: (typeof PORTFOLIO_COLUMNS)[number][] = columns?.length
    ? columns
    : [...DEFAULT_PORTFOLIO_COLUMNS];

  const availableMenuOptions: AssetOptionEnum[] = menuOptions?.length
    ? menuOptions
    : Object.values(AssetOptionEnum);

  const { user } = useAuth();

  const { dispatch: dispatchModal, closeModal } = useModalContext();

  const { t } = useTranslation();

  const { allStrategies, refetch: refetchStrategies } = useUserStrategies();

  const {
    assetIdToUpdate,
    loadingSetAssetEntryPrice,
    loadingSetAssetUnit,
    loadingSetLastPrice,
    handleOnBlur,
    handleChange,
  } = useAssetInputChanges(refetchStrategies);

  const strategy = allStrategies.find((s) => s.id === strategyId);
  const items = strategy?.strategyAssets?.items || [];
  const currency = strategy?.currency || CurrencyCodeEnum.USD;
  const currencySymbol = getSymbol(currency);

  const { direction, type, getSortValue, handleSort } = useSort([
    ...availableColumns,
  ]);

  // Move VAR.ALL at first place
  const intervals: (typeof INTERVALS)[number][] = INTERVALS.filter(
    (interval) => interval !== 'VAR_LAST'
  ).reduce((acc, interval) => {
    if (interval === INTERVALS[INTERVALS.length - 1]) {
      return [interval, ...acc];
    }
    return [...acc, interval];
  }, [] as (typeof INTERVALS)[number][]);

  const handleClickAsset = (assetId: string) => {
    const asset = items.find(({ id }) => id === assetId);

    asset &&
      setDetailAsset(
        <AssetDetail
          asset={asset}
          currency={currency.toLocaleLowerCase() as TTickerCurrency}
          onChange={handleChange}
          goBack={() => setDetailAsset(undefined)}
        />
      );
  };

  const [groups, setGroups] = useState<StrategyAssetsParentGroup[]>([]);
  const [categories, setCategories] = useState<StrategyAssetsParentGroup[]>([]);
  const [subCategoriesMap, setSubCategoriesMap] = useState<
    Record<string, number>
  >({});
  const [categoryVisibilityMap, setCategoryVisibilityMap] = useState<
    Record<string, boolean>
  >({});
  const [subCategoryVisibilityMap, setSubCategoryVisibilityMap] = useState<
    Record<string, boolean>
  >({});
  const [detailAsset, setDetailAsset] = useState<JSX.Element>();
  const [selectedAssets, setSelected] = useState<string[]>([]);
  const [editing, setEditing] = useState<string>('');

  useEffect(() => {
    const assetGroups = aggregateByGroupAndAsset({
      assets: items,
      currency,
      withUnit: true,
    });
    setGroups(assetGroups);
  }, [items]);

  // SORT
  useEffect(() => {
    const sortedGroups = [...groups];
    const totalValueStrategy = sortedGroups.reduce(
      (a, c) => a + c.totalValue,
      0
    );
    const initialTotalValueStrategy = sortedGroups.reduce(
      (a, c) => a + c.entryValue,
      0
    );

    canSortCategories(type) &&
      sortedGroups.sort((a, b) => {
        let [arg1, arg2]: (number | string)[] = [a.title, b.title];

        type === 'INVESTED' && ([arg1, arg2] = [a.entryValue, b.entryValue]);
        (intervals as string[]).includes(type) &&
          ([arg1, arg2] = [
            computePercentage(a.timeFrames[type], a.totalValue),
            computePercentage(b.timeFrames[type], b.totalValue),
          ]);
        type === 'TOT VALUE' && ([arg1, arg2] = [a.totalValue, b.totalValue]);
        type === 'ALLOCATION' &&
          ([arg1, arg2] = [
            computePercentage(totalValueStrategy, a.totalValue),
            computePercentage(totalValueStrategy, b.totalValue),
          ]);
        type === 'INITIAL_ALLOCATION' &&
          ([arg1, arg2] = [
            computePercentage(initialTotalValueStrategy, a.entryValue),
            computePercentage(initialTotalValueStrategy, b.entryValue),
          ]);

        return getSortValue(arg1, arg2);
      });

    // SORT ASSETS OF STRATEGY
    sortedGroups.forEach(({ strategyAssetsGroup }) => {
      strategyAssetsGroup.forEach(({ assets }) => {
        assets.sort((a, b) => {
          const lastPriceA =
            a.asset.history[0]?.[currency.toLocaleLowerCase()] ?? 0;
          const lastPriceB =
            a.asset.history[0]?.[currency.toLocaleLowerCase()] ?? 0;

          let [arg1, arg2]: (number | string)[] = [a.name, b.name];

          type === 'QUANTITY' && ([arg1, arg2] = [a.unit, b.unit]);
          type === 'INVESTED' &&
            ([arg1, arg2] = [a.unit * a.entryPrice, b.unit * b.entryPrice]);
          type === 'ENTRY PRICE' &&
            ([arg1, arg2] = [a.entryPrice, b.entryPrice]);
          type === 'LAST PRICE' && ([arg1, arg2] = [lastPriceA, lastPriceB]);
          type === 'TOT VALUE' &&
            ([arg1, arg2] = [a.unit * lastPriceA, b.unit * lastPriceB]);
          type === 'ALLOCATION' &&
            ([arg1, arg2] = [
              computePercentage(totalValueStrategy, a.unit * lastPriceA),
              computePercentage(totalValueStrategy, b.unit * lastPriceB),
            ]);
          type === 'INITIAL_ALLOCATION' &&
            ([arg1, arg2] = [
              computePercentage(
                initialTotalValueStrategy,
                a.unit * a.entryPrice
              ),
              computePercentage(
                initialTotalValueStrategy,
                b.unit * b.entryPrice
              ),
            ]);

          if ((intervals as string[]).includes(type)) {
            const historyA = a.asset.history;
            const historyB = b.asset.history;

            const purchasedAtA = a.purchasedAt;
            const purchasedAtB = b.purchasedAt;

            const varA = getVariation(
              currency,
              historyA,
              purchasedAtA,
              a.entryPrice,
              1
            )[type];
            const varB = getVariation(
              currency,
              historyB,
              purchasedAtB,
              b.entryPrice,
              1
            )[type];

            [arg1, arg2] = [
              computePercentage(varA, lastPriceA),
              computePercentage(varB, lastPriceB),
            ];
          }

          return getSortValue(arg1, arg2);
        });
      });
    });

    const categoryVisibilityMap: Record<string, boolean> = {};
    const subCategoryVisibilityMap: Record<string, boolean> = {};
    const subCategoriesMap: Record<string, number> = {};
    sortedGroups.forEach((g) => {
      g.strategyAssetsGroup.forEach((ag) => {
        subCategoryVisibilityMap[ag.title] = true;
        ag.assets.forEach((a) => {
          subCategoriesMap[a.assetId] =
            (subCategoriesMap?.[a.assetId] || 0) + 1;
        });
      });
      categoryVisibilityMap[g.title] = true;
    });

    setCategoryVisibilityMap(categoryVisibilityMap);
    setSubCategoryVisibilityMap(subCategoryVisibilityMap);
    setCategories(sortedGroups);
    setSubCategoriesMap(subCategoriesMap);
  }, [direction, groups, type]);

  const {
    totalValue: totalValueStrategy,
    totalEntryValue: initialTotalValueStrategy,
    variations: strategyVariations,
  } = getTotalValues(
    items,
    currency.toLocaleLowerCase() as TTickerCurrency,
    strategy?.createdAt || formatAWSDate(new Date())
  );

  useEffect(() => {
    setSelected([]);
  }, [items]);

  const handleCheckAsset = ([_, assetId]: [number, string]) => {
    const isSelected = selectedAssets.includes(assetId);

    setSelected(
      isSelected
        ? selectedAssets.filter((id) => id !== assetId)
        : [...selectedAssets, assetId]
    );
  };

  const handleCheckCategory = (
    assetsIds: string[],
    allAssetsSelected: boolean
  ) => {
    setSelected(
      allAssetsSelected
        ? selectedAssets.filter((id) => !assetsIds.includes(id))
        : Array.from(new Set([...selectedAssets, ...assetsIds]))
    );
  };

  const isAllSelected =
    selectedAssets.length > 0 && selectedAssets.length === items.length;

  const handleAllCheck = () => {
    setSelected(isAllSelected ? [] : items.map(({ id }) => id));
  };

  let header = availableColumns.map((columnName, j) => (
    <div
      key={'portfolio-column' + j}
      className={`
        flex
        flex-1
        items-center
        ${!j ? (width === 1 ? 'w-44' : 'w-60') : 'w-36'}
        ${j === 1 && 'pl-5'}
        border-b
        border-grey-10
        h-10
        items-center
        bg-white
        dark:bg-grey-90
        dark:border-grey-70
      `}
    >
      {useCheckboxes && j === 0 && (
        <Checkbox
          className="mx-3"
          shape="square"
          size={'small'}
          value={'ALL'}
          isCheck={isAllSelected}
          onClick={handleAllCheck}
        />
      )}
      <Column
        className={`
          flex
          items-center
          w-full
          h-full
          dark:bg-grey-90
          ${!j && 'border-l pl-3'}
          ${!j && width > 1 && 'border-r'}
          border-grey-10
          dark:border-grey-70
        `}
        element={
          <Sort
            className={`
              flex
              items-center
              font-medium
              w-full
              h-full
              uppercase
            `}
            columnName={t('widget.portfolio.' + columnName)}
            key={columnName}
            type={type}
            handleSort={handleSort}
          />
        }
        span={1}
      />
    </div>
  ));

  /*** TOTAL ROWS ***/
  let totals = [
    // ASSET INFO
    availableColumns.includes('ASSET') && (
      <>
        {useCheckboxes && <div className="w-3 mx-3" />}
        <div className="flex items-center border-l border-grey-10 pl-3 h-full">
          <div className="flex flex-col w-full">
            <span className="font-medium">{'Total'}</span>
            <CustomNumber
              className="text-grey-50 text-xs"
              suffix=" assets"
              displayType={'text'}
              value={items.length}
            />
          </div>
        </div>
      </>
    ),

    // QUANTITY
    availableColumns.includes('QUANTITY') && <></>,

    // CURRENCY
    // <></>,

    // ENTRY PRICE
    availableColumns.includes('ENTRY PRICE') && <></>,

    // INVESTED
    availableColumns.includes('INVESTED') && (
      <CustomNumber
        className="font-medium text-sm bg-lime cursor-default px-4"
        suffix={currencySymbol}
        displayType={'text'}
        value={items.reduce((a, b) => a + b.unit * b.entryPrice, 0)}
      />
    ),

    // LAST PRICE
    availableColumns.includes('LAST PRICE') && <></>,

    // TOT VALUE
    availableColumns.includes('TOT VALUE') && (
      <CustomNumber
        className="font-medium text-sm bg-lime cursor-default px-4"
        displayType={'text'}
        suffix={currencySymbol}
        value={totalValueStrategy}
      />
    ),

    // INITIAL_ALLOCATION
    availableColumns.includes('INITIAL_ALLOCATION') && (
      <CustomNumber
        displayType={'text'}
        className="font-medium text-sm w-full flex bg-lime cursor-default px-4"
        value={100}
        suffix="%"
      />
    ),

    // ALLOCATION
    availableColumns.includes('ALLOCATION') && (
      <CustomNumber
        displayType={'text'}
        className="font-medium text-sm w-full flex bg-lime cursor-default px-4"
        value={100}
        suffix="%"
      />
    ),

    // VAR's
    ...intervals
      .filter((interval) => (availableColumns as string[]).includes(interval))
      .map((interval) => (
        <ProfitLoss
          displayType={'text'}
          className={'px-4'}
          canUseDarkMode={false}
          key={interval}
          symbol={currencySymbol}
          percentage={computePercentage(
            strategyVariations[interval],
            totalValueStrategy,
            100
          )}
          prefixValue={
            computePercentage(
              strategyVariations[interval],
              totalValueStrategy,
              100
            ) > 0
              ? '+'
              : undefined
          }
          value={
            strategyVariations[interval] === 0
              ? 0
              : totalValueStrategy - strategyVariations[interval]
          }
        />
      )),
  ]
    .filter((element) => element)
    .map((element, i) => (
      <div
        className={`
        flex
        flex-1
        items-center
        border-b
        border-grey-10
        h-16
        bg-lime
        dark:text-black
        ${!i ? (width === 1 ? 'w-44' : 'w-60') : 'w-36'}
        ${!i && width > 1 && 'border-r'}
      `}
      >
        {element}
      </div>
    ));

  width === 1 &&
    (header = header.filter((_, i) => !i || i > availableColumns.length - 3));
  width === 1 &&
    (totals = totals.filter((_, i) => !i || i > availableColumns.length - 3));

  const table = [header, items.length > 1 ? totals : []];

  const renderItemDropdown = (strategyAsset: IStrategyAsset) => {
    const isEditable =
      strategyAsset.connectionType === ConnectionTypeEnum.MANUAL;
    const dropdownItems = availableMenuOptions.filter(
      (option) => isEditable || option === AssetOptionEnum.DELETE
    );
    return (
      <DropdownMenu
        buttonClassName=""
        hideOnOutsideClick={true}
        hideOnMouse={true}
        dropdownClassName={`flex pr-4 items-center`}
        dropdownTooltipClassName={`
                    bg-white
                    border-solid
                    border
                    border-grey-30
                  `}
        dropdownTooltipXOffset={-75}
        dropdownTooltipYOffset={-40}
        dropdownItems={dropdownItems.map((k) =>
          t(`strategies.assetOption.${k}`)
        )}
        dropdownItemsData={[
          {
            key: AssetOptionEnum.EDIT_ASSET,
            title: t('strategies.editAsset'),
            el: (
              <>
                <UpsertAsset
                  strategyAsset={strategyAsset}
                  strategyId={strategyAsset.strategyId}
                  selectedConnectionType={strategyAsset.connectionType}
                  action={'EDIT'}
                  assetCategory={{
                    id: strategyAsset.strategyAssetCategoryId,
                    title: strategyAsset.strategyAssetCategory.title,
                    type: strategyAsset.strategyAssetCategory.type,
                    key: strategyAsset.strategyAssetCategory.key,
                  }}
                  asset={strategyAsset.asset}
                  onCloseClick={closeModal}
                />
              </>
            ),
            fullScreen: false,
            isClosable: true,
            infoTooltip: t(
              'strategies.assetAdvancedSettings.advancedSettingsTooltip'
            ),
          },
          {
            key: AssetOptionEnum.COPY_TO,
            title: '',
            el: (
              <CopyTo
                strategyAssetIds={[strategyAsset.id]}
                strategyId={strategyId}
              />
            ),
            fullScreen: true,
            isClosable: false,
          },
          {
            key: AssetOptionEnum.DUPLICATE,
            title: '',
            el: <DuplicateAssetModal strategyAssetId={strategyAsset.id} />,
            fullScreen: false,
            height: 'auto',
            isClosable: true,
          },
          {
            key: AssetOptionEnum.DELETE,
            title: '',
            el: (
              <DeleteAssetModal
                strategyAssetIds={[strategyAsset.id]}
                strategyId={strategyId}
              />
            ),
            fullScreen: false,
            height: 'auto',
            isClosable: true,
          },
        ].filter(({ key }) => dropdownItems.includes(key))}
        arrowPosition="right"
        handleClick={(_, obj) => {
          dispatchModal({
            customMerlinLogoColor: obj.color,
            element: obj.el,
            handleMerlinLogo: true,
            isFullScreenElement: obj.fullScreen,
            isClosable: obj.isClosable,
            title: obj.title,
            height: obj.height,
            infoTooltip: obj.infoTooltip,
          });
        }}
      >
        <Icon svgName="options" className="cursor-pointer" />
      </DropdownMenu>
    );
  };

  /*** CATEGORIES ROWS ***/
  categories.forEach(
    (
      {
        strategyAssetsGroup,
        entryValue,
        title: categoryTitle,
        // key: categoryKey,
        totalValue,
        timeFrames,
      },
      categoryIndex
    ) => {
      const categoryAssets = strategyAssetsGroup.flatMap((group) =>
        group.assets.map((asset) => asset)
      );

      const isCategoryChecked = categoryAssets.every((asset) =>
        selectedAssets.includes(asset.id)
      );

      let category = [
        // ASSET INFO + CHECKBOX
        availableColumns.includes('ASSET') && (
          <>
            {useCheckboxes && (
              <Checkbox
                className="mx-3"
                shape="square"
                value={categoryIndex}
                isCheck={isCategoryChecked}
                size={'small'}
                onClick={() =>
                  handleCheckCategory(
                    categoryAssets.map(({ id }) => id),
                    isCategoryChecked
                  )
                }
              />
            )}
            <div className="flex items-center border-l border-grey-10 pl-3 h-full w-full dark:bg-grey-90 dark:border-grey-70">
              <div className="flex flex-col w-full">
                <span className="font-medium">{categoryTitle}</span>
                <span className="text-grey-50 text-xs">
                  {categoryAssets.length + ' assets'}
                </span>
              </div>
              <Icon
                svgName={
                  categoryVisibilityMap[categoryTitle] ? 'arrowUp' : 'arrowDown'
                }
                className="ml-auto mr-5 cursor-pointer"
                onClick={() => {
                  setCategoryVisibilityMap((obj) => ({
                    ...obj,
                    [categoryTitle]: !obj[categoryTitle],
                  }));
                }}
              />
            </div>
          </>
        ),

        // QUANTITY
        availableColumns.includes('QUANTITY') && <></>,

        // CURRENCY
        // <></>,

        // ENTRY PRICE
        availableColumns.includes('ENTRY PRICE') && <></>,

        // INVESTED
        availableColumns.includes('INVESTED') && (
          <CustomNumber
            displayType={'text'}
            className="font-medium text-sm cursor-default px-4 bg-grey-5 dark:bg-grey-90"
            suffix={currencySymbol}
            value={entryValue}
          />
        ),

        // LAST PRICE
        availableColumns.includes('LAST PRICE') && <></>,

        // TOT VALUE
        availableColumns.includes('TOT VALUE') && (
          <CustomNumber
            displayType={'text'}
            className="font-medium text-sm cursor-default px-4 bg-grey-5 dark:bg-grey-90"
            suffix={currencySymbol}
            value={totalValue}
          />
        ),

        // INITIAL_ALLOCATION
        availableColumns.includes('INITIAL_ALLOCATION') && (
          <CustomNumber
            displayType={'text'}
            className="font-medium text-sm w-full px-4 flex cursor-default bg-grey-5 dark:bg-grey-90"
            value={computePercentage(initialTotalValueStrategy, entryValue)}
            suffix="%"
          />
        ),

        // ALLOCATION
        availableColumns.includes('ALLOCATION') && (
          <CustomNumber
            displayType={'text'}
            className="font-medium text-sm w-full px-4 flex cursor-default bg-grey-5 dark:bg-grey-90"
            value={computePercentage(totalValueStrategy, totalValue)}
            suffix="%"
          />
        ),

        // VAR's
        ...intervals
          .filter((interval) =>
            (availableColumns as string[]).includes(interval)
          )
          .map((interval) => (
            <>
              <ProfitLoss
                displayType={'text'}
                className={'px-4'}
                key={interval}
                symbol={currencySymbol}
                percentage={computePercentage(
                  timeFrames[interval],
                  totalValue,
                  100
                )}
                prefixValue={
                  computePercentage(timeFrames[interval], totalValue, 100) > 0
                    ? '+'
                    : undefined
                }
                value={
                  timeFrames[interval] === 0
                    ? 0
                    : totalValue - timeFrames[interval]
                }
              />
            </>
          )),
      ]
        .filter((element) => element)
        .map((element, i) => (
          <div
            className={`
            flex
            flex-1
            items-center
            border-b
            border-grey-10
            h-16
            bg-grey-5
            ${!i ? (width === 1 ? 'w-44' : 'w-60') : 'w-36'}
            ${!i && width > 1 && 'border-r'}
            dark:bg-grey-90
            dark:text-white
            dark:border-grey-70
          `}
          >
            {element}
          </div>
        ));

      width === 1 &&
        (category = category.filter(
          (_, i) => !i || i > availableColumns.length - 3
        ));

      categories.length > 1 && table.push(category);

      /*** SUBCATEGORIES ROW ***/
      strategyAssetsGroup.forEach(
        (
          {
            assets,
            entryValue,
            title: subCategoryTitle,
            totalValue,
            timeFrames,
          },
          categoryIndex
        ) => {
          const isSubcategoryChecked = assets.every(({ id }) =>
            selectedAssets.includes(id)
          );

          let subCategory = [
            // ASSET INFO + CHECKBOX
            availableColumns.includes('ASSET') && (
              <>
                {useCheckboxes && (
                  <Checkbox
                    className="mx-3"
                    shape="square"
                    value={categoryIndex}
                    isCheck={isSubcategoryChecked}
                    size={'small'}
                    onClick={() =>
                      handleCheckCategory(
                        assets.map(({ id }) => id),
                        isSubcategoryChecked
                      )
                    }
                  />
                )}
                <div className="flex bg-white items-center border-l border-grey-10 pl-3 h-full w-full dark:bg-grey-90 dark:border-grey-70">
                  <Asset
                    id={assets[0].assetId}
                    connectionType={assets[0].connectionType}
                    type={assets[0].asset.type || ''}
                    symbol={`${assets[0].asset.symbol} (${assets.length})`}
                    name={assets[0].asset.name}
                    logo={assets[0].asset.logo}
                    titleClassName={'text-black text-xs font-semibold'}
                    subtitleClassName={'text-black text-xs font-semibold'}
                  />
                  <Icon
                    svgName={
                      subCategoryVisibilityMap[subCategoryTitle]
                        ? 'arrowUp'
                        : 'arrowDown'
                    }
                    className="ml-auto mr-5 cursor-pointer"
                    onClick={() => {
                      setSubCategoryVisibilityMap((obj) => ({
                        ...obj,
                        [subCategoryTitle]: !obj[subCategoryTitle],
                      }));
                    }}
                  />
                </div>
              </>
            ),

            // QUANTITY
            availableColumns.includes('QUANTITY') && (
              <>
                {loadingSetAssetUnit && assetIdToUpdate === subCategoryTitle ? (
                  <Loading status={true} size={'small'} className={'mr-6'} />
                ) : (
                  <CustomNumber
                    displayType={'text'}
                    className="cursor-default text-sm px-4"
                    value={assets.reduce((a, b) => a + b.unit, 0)}
                  />
                )}
              </>
            ),

            // CURRENCY
            // <></>,

            // ENTRY PRICE
            availableColumns.includes('ENTRY PRICE') && (
              <>
                <CustomNumber
                  displayType={'text'}
                  className="font-medium text-sm cursor-default px-4"
                  suffix={currencySymbol}
                  value={calculateWeightedAverage(assets)}
                />
              </>
            ),

            // INVESTED
            availableColumns.includes('INVESTED') && (
              <CustomNumber
                displayType={'text'}
                className="font-medium text-sm cursor-default px-4"
                suffix={currencySymbol}
                value={entryValue}
              />
            ),

            // LAST PRICE
            availableColumns.includes('LAST PRICE') && <></>,

            // TOT VALUE
            availableColumns.includes('TOT VALUE') && (
              <CustomNumber
                displayType={'text'}
                className="font-medium text-sm cursor-default px-4 "
                suffix={currencySymbol}
                value={totalValue}
              />
            ),

            // INITIAL_ALLOCATION
            availableColumns.includes('INITIAL_ALLOCATION') && (
              <CustomNumber
                displayType={'text'}
                className="font-medium text-sm w-full px-4 flex cursor-default"
                value={computePercentage(initialTotalValueStrategy, entryValue)}
                suffix="%"
              />
            ),

            // ALLOCATION
            availableColumns.includes('ALLOCATION') && (
              <CustomNumber
                displayType={'text'}
                className="font-medium text-sm w-full px-4 flex cursor-default"
                value={computePercentage(totalValueStrategy, totalValue)}
                suffix="%"
              />
            ),

            // VAR's
            ...intervals
              .filter((interval) =>
                (availableColumns as string[]).includes(interval)
              )
              .map((interval) => (
                <>
                  <ProfitLoss
                    displayType={'text'}
                    className={'px-4'}
                    key={interval}
                    symbol={currencySymbol}
                    percentage={computePercentage(
                      timeFrames[interval],
                      totalValue,
                      100
                    )}
                    prefixValue={
                      computePercentage(timeFrames[interval], totalValue, 100) >
                      0
                        ? '+'
                        : undefined
                    }
                    value={
                      timeFrames[interval] === 0
                        ? 0
                        : totalValue - timeFrames[interval]
                    }
                  />
                </>
              )),
          ]
            .filter((element) => element)
            .map((element, i) => (
              <div
                className={`
            flex
            flex-1
            items-center
            border-b
            border-grey-10
            h-16
            ${!i ? (width === 1 ? 'w-44' : 'w-60') : 'w-36'}
            ${!i && width > 1 && 'border-r'}
          `}
              >
                {element}
              </div>
            ));

          width === 1 &&
            (subCategory = subCategory.filter(
              (_, i) => !i || i > availableColumns.length - 3
            ));

          categoryVisibilityMap[categoryTitle] &&
            subCategoriesMap[subCategoryTitle] > 1 &&
            table.push(subCategory);

          // ASSETS
          assets.forEach((strategyAsset) => {
            const {
              asset: {
                logo,
                type: assetType,
                name: assetName,
                symbol,
                history,
                exchange,
                id: assetId,
              },
              connectionType,
              entryPrice,
              id,
              unit,
            } = strategyAsset;
            const isCheck = selectedAssets.includes(id);
            const lastPrice =
              history[0]?.[currency.toLocaleLowerCase() as TTickerCurrency] ??
              0;

            const previousPrice =
              history[1]?.[currency.toLocaleLowerCase() as TTickerCurrency] ??
              0;

            const { variations } = getTotalValues(
              [strategyAsset],
              currency.toLocaleLowerCase() as TTickerCurrency,
              // the date is not correct, but it does not affect the calculation of variations
              moment().format('YYYY-MM-DD')
            );

            // logger.log('Portfolio:prices', name, assetId, lastPrice, previousPrice);

            const lastVar = computePercentage(previousPrice, lastPrice, 100);
            // logger.log('Portfolio:AssetVariations', name, variations);

            /*** ASSET ROWS ***/
            let assetElement = [
              // CHECKBOX + ASSET INFO
              availableColumns.includes('ASSET') && (
                <>
                  {useCheckboxes && (
                    <Checkbox
                      className="mx-3"
                      shape="square"
                      value={[categoryIndex, id]}
                      isCheck={isCheck}
                      size={'small'}
                      onClick={handleCheckAsset}
                    />
                  )}
                  <div
                    className={`flex w-full items-center border-l border-grey-10 ${
                      subCategoriesMap[assetId] > 1 ? 'pl-6' : 'pl-3'
                    } h-full dark:border-grey-70`}
                  >
                    <Asset
                      id={assetId}
                      connectionType={connectionType}
                      type={assetType || ''}
                      symbol={symbol}
                      name={assetName}
                      exchange={exchange}
                      logo={logo}
                    />
                  </div>
                </>
              ),

              // QUANTITY
              availableColumns.includes('QUANTITY') && (
                <>
                  {loadingSetAssetUnit && assetIdToUpdate === id ? (
                    <Loading status={true} size={'small'} className={'mr-6'} />
                  ) : (
                    <CustomNumber
                      className={`
                  w-full
                  h-full
                  flex
                  px-4
                  ${
                    connectionType === 'MANUAL'
                      ? 'hover:border hover:border-grey-30 hover:bg-lime-light-1 cursor-text'
                      : 'cursor-default'
                  }
                  focus:font-medium
                  text-sm
                  dark:text-white
                `}
                      id={id}
                      name="unit"
                      onBlur={(e) => handleOnBlur(e, unit)}
                      onKeyUp={(e) => handleChange(e, unit)}
                      readOnly={connectionType !== 'MANUAL'}
                      value={unit}
                    />
                  )}
                </>
              ),

              // ENTRY PRICE
              availableColumns.includes('ENTRY PRICE') && (
                <>
                  {loadingSetAssetEntryPrice && assetIdToUpdate === id ? (
                    <Loading status={true} size={'small'} className={'mr-6'} />
                  ) : (
                    <CustomNumber
                      className={`
                  w-full
                  h-full
                  px-4
                  flex
                  ${
                    connectionType === 'MANUAL'
                      ? 'hover:border hover:border-grey-30 hover:bg-lime-light-1 cursor-text'
                      : 'cursor-default'
                  }
                  focus:font-medium
                  text-sm
                `}
                      id={id}
                      name="entryPrice"
                      onBlur={(e) => handleOnBlur(e, entryPrice)}
                      onKeyUp={(e) => handleChange(e, entryPrice)}
                      readOnly={connectionType !== 'MANUAL'}
                      suffix={currencySymbol}
                      value={entryPrice}
                    />
                  )}
                </>
              ),

              // INVESTED
              availableColumns.includes('INVESTED') && (
                <>
                  {(loadingSetAssetEntryPrice || loadingSetAssetUnit) &&
                  assetIdToUpdate === id ? (
                    <Loading status={true} size={'small'} className={'mr-6'} />
                  ) : (
                    <CustomNumber
                      displayType={'text'}
                      className="cursor-default text-sm px-4"
                      suffix={currencySymbol}
                      value={unit * entryPrice}
                    />
                  )}
                </>
              ),

              // LAST_PRICE
              availableColumns.includes('LAST PRICE') && (
                <>
                  {loadingSetLastPrice && assetIdToUpdate === id ? (
                    <Loading status={true} size={'small'} className={'mr-6'} />
                  ) : editing === id ? (
                    <>
                      <CustomNumber
                        className={`
                  w-full
                  h-full
                  flex
                  px-4
                  ${
                    connectionType === 'MANUAL'
                      ? 'hover:border hover:border-grey-30 hover:bg-lime-light-1 cursor-text'
                      : 'cursor-default'
                  }
                  focus:font-medium
                  text-sm
                `}
                        id={id}
                        name="lastPrice"
                        onBlur={(e) => {
                          logger.log('AssetInputChange:onBlur');
                          handleOnBlur(e, lastPrice)?.finally(() =>
                            setEditing('')
                          );
                        }}
                        onKeyUp={(e) => {
                          logger.log('AssetInputChange:AssetInputChange');
                          handleChange(e, lastPrice)?.then(() =>
                            setEditing('')
                          );
                        }}
                        readOnly={connectionType !== 'MANUAL'}
                        suffix={currencySymbol}
                        value={lastPrice}
                      />
                    </>
                  ) : (
                    <ProfitLoss
                      displayType={'text'}
                      className={`flex w-full h-full justify-center px-4
                 ${
                   assetType === 'USER'
                     ? 'hover:border hover:border-grey-30 hover:bg-lime-light-1 cursor-text'
                     : ''
                 }`}
                      symbol={currencySymbol}
                      percentage={lastVar}
                      value={lastPrice}
                      size={'small'}
                      handleClick={() => {
                        if (assetType === 'USER') {
                          setEditing(id);
                        }
                        logger.log('Portfolio:clickLastPrice', id);
                      }}
                    />
                  )}
                </>
              ),

              // TOT VALUE
              availableColumns.includes('TOT VALUE') && (
                <CustomNumber
                  displayType={'text'}
                  className="cursor-default text-sm px-4"
                  suffix={currencySymbol}
                  value={unit * lastPrice}
                />
              ),

              // INITIAL_ALLOCATION
              availableColumns.includes('INITIAL_ALLOCATION') && (
                <div className="flex items-center w-full px-4">
                  <CustomNumber
                    displayType={'text'}
                    className={`
               w-full
               flex
               cursor-default
             `}
                    value={computePercentage(
                      initialTotalValueStrategy,
                      unit * entryPrice
                    )}
                    suffix="%"
                  />
                </div>
              ),

              // ALLOCATION
              availableColumns.includes('ALLOCATION') && (
                <div className="flex items-center w-full px-4">
                  <CustomNumber
                    displayType={'text'}
                    className={`
               w-full
               flex
               cursor-default
             `}
                    value={computePercentage(
                      totalValueStrategy,
                      unit * lastPrice
                    )}
                    suffix="%"
                  />
                </div>
              ),

              // VAR's
              ...intervals
                .filter((interval) =>
                  (availableColumns as string[]).includes(interval)
                )
                .map((interval) => (
                  <div className="flex items-center w-full px-4 justify-between">
                    <ProfitLoss
                      displayType={'text'}
                      key={interval}
                      symbol={currencySymbol}
                      percentage={computePercentage(
                        variations[interval],
                        unit * lastPrice,
                        100
                      )}
                      prefixValue={
                        computePercentage(
                          variations[interval],
                          unit * lastPrice,
                          100
                        ) > 0
                          ? '+'
                          : undefined
                      }
                      value={
                        variations[interval] === 0
                          ? 0
                          : unit * lastPrice - variations[interval]
                      }
                    />
                  </div>
                )),
            ]
              .filter((element) => element)
              .map((element, j, arr) => (
                <div
                  key={`asset_${id}`}
                  className={`
              flex
              flex-1
              items-center
              ${!j ? (width === 1 ? 'w-44' : 'w-60') : 'w-36'}
              ${!j && width > 1 && 'border-r'}
              border-b
              border-grey-10
              h-16
              items-center
              dark:bg-grey-80
              dark:border-grey-70
            `}
                >
                  {element}
                  {j === arr.length - 1 && renderItemDropdown(strategyAsset)}
                </div>
              ));

            width === 1 &&
              (assetElement = assetElement.filter(
                (_, i) => !i || i > availableColumns.length - 3
              ));

            categoryVisibilityMap[categoryTitle] &&
              subCategoryVisibilityMap[subCategoryTitle] &&
              table.push(assetElement);
          });
        }
      );
    }
  );

  return (
    <div className="flex flex-col w-full overflow-hidden flex-grow-1">
      {detailAsset || (
        <>
          {/* add asset + timeframe */}
          <div
            className={`
              flex
              overflow-hidden
              w-full
              min-h-16
              items-center
              pl-5
              border-b
              border-grey-10
              dark:border-grey-70
            `}
          >
            <ActionButton
              size={'large'}
              className={'h-8 w-8 mr-3 cursor-pointer'}
              highlightIcon={true}
              onClick={() => {
                const canAddAsset =
                  user?.membership.membershipPlan.features.strategyAssets ===
                    -1 ||
                  items.length <
                    user!.membership.membershipPlan.features.strategyAssets;

                dispatchModal({
                  className: canAddAsset ? '' : 'bg-opacity-0',
                  isFullScreenElement: !canAddAsset,
                  element: canAddAsset ? (
                    <UpsertAsset
                      action={'CREATE'}
                      strategyId={strategyId}
                      onCloseClick={closeModal}
                    />
                  ) : (
                    <UpgradeSubscription
                      onCloseClick={closeModal}
                      isOpen
                      feature="ASSETS"
                      viewMode="modal"
                    />
                  ),
                  title: canAddAsset
                    ? t('strategies.newStrategy.addAsset')
                    : '',
                });
              }}
            />
            <span className="font-medium">Add asset</span>
          </div>

          {/* table */}
          <div
            className={`
              flex
              flex-col
              flex-grow-1
              ${height === 1 ? 60 : 90}
              overflow-x-scroll
            `}
          >
            {table.map((col, i) => {
              const isAsset =
                col.length > 0 &&
                col[0].key !== null &&
                col[0].key.toString().includes('asset_');

              return (
                <div
                  key={'portfolio-table-row-' + i}
                  className={`
                    flex
                    ${!i && 'sticky top-0 z-20'}
                    ${
                      width === 1 &&
                      isAsset &&
                      'hover:border hover:border-grey-50'
                    }
                  `}
                  onClick={() => {
                    width === 1 &&
                      isAsset &&
                      handleClickAsset(col[0].key!.toString().slice(6));
                  }}
                >
                  {col.map((el, colIndex) => (
                    <div
                      key={'portfolio-table-cell-' + colIndex}
                      className={`
                        flex
                        w-full
                        ${!colIndex && 'sticky left-0 z-10'}
                        ${isAsset && 'bg-white'}
                      `}
                    >
                      {el}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          {useCheckboxes && (
            <MultiSelected
              assetsIds={selectedAssets}
              isAllSelected={isAllSelected}
              strategyId={strategyId}
              handleAllCheck={handleAllCheck}
            />
          )}
        </>
      )}
    </div>
  );
};
